/**
 * A run's bag as the airship packs it (slice 8f, ADR 0041; ADR 0027, ADR 0039 D3): which items a bag takes, the
 * server's own choice when the player makes none (`autoBag`), and whether a chosen bag is one the rules allow
 * (`bagRefusal`). The server decides with these (`POST /api/runs`); the web client's packing panel draws with them
 * (its Auto row shows the server's choice, a stack the server would refuse is never offered), as `rosterParty` is
 * shared. Nothing here reads storage: the caller passes it.
 *
 * - **What a bag takes:** an item usable in the dungeon (a consumable whose `use.contexts` has `dungeon`) or a charm
 *   (it acts by itself from the bag); never equipment, a trade good or an unknown id. Each item once (game-core holds
 *   an item once in a starting bag), each count within `1..stackSize.dungeon`, at most `rules.dungeon.bagSlots` stacks.
 * - **Auto:** storage in item id order (code-unit order of the ids), every item a bag takes, a full dungeon stack of
 *   each (or what storage holds), until the slots are full.
 */
import type { Item } from '@ab/content-schema';
import type { BagEntry } from '@ab/game-core';

type Items = Readonly<Record<string, Item>>;
type Stack = { readonly item: string; readonly count: number };

/** Why a chosen bag is refused: the contract's `badBagReasons`, in the order they are checked. */
export type BagRefusal = 'too_many_stacks' | 'unknown_item' | 'not_usable' | 'duplicate' | 'too_many' | 'not_in_storage';

const itemOf = (items: Items, id: string): Item | undefined => (Object.hasOwn(items, id) ? items[id] : undefined);

/** Whether an item goes into a run's bag: usable in the dungeon, or a charm. */
export const bagItem = (items: Items, id: string): boolean => {
  const def = itemOf(items, id);
  return def !== undefined && (def.category === 'charm' || (def.use?.contexts.includes('dungeon') ?? false));
};

/** How many of an item one bag stack holds (`stackSize.dungeon`; 1 for an unknown id). */
export const dungeonStack = (items: Items, id: string): number => itemOf(items, id)?.stackSize.dungeon ?? 1;

const byItem = (a: Stack, b: Stack): number => (a.item < b.item ? -1 : a.item > b.item ? 1 : 0);

/** The bag the server packs when the player chose none: see the module's comment. */
export const autoBag = (items: Items, bagSlots: number, storage: readonly Stack[]): BagEntry[] => {
  const bag: BagEntry[] = [];
  for (const s of [...storage].sort(byItem)) {
    if (bag.length >= bagSlots) break;
    if (s.count < 1 || !bagItem(items, s.item) || bag.some((b) => b.item === s.item)) continue;
    bag.push({ item: s.item, count: Math.min(s.count, dungeonStack(items, s.item)) });
  }
  return bag;
};

/**
 * Why a chosen bag is refused, or null when the rules allow it: more stacks than the slots, an unknown item, one a bag
 * never takes, an item twice, a count outside `1..stackSize.dungeon`, and (with `storage`) more than storage holds.
 */
export const bagRefusal = (items: Items, bagSlots: number, bag: readonly Stack[], storage?: readonly Stack[]): BagRefusal | null => {
  if (bag.length > bagSlots) return 'too_many_stacks';
  const seen = new Set<string>();
  for (const s of bag) {
    if (itemOf(items, s.item) === undefined) return 'unknown_item';
    if (!bagItem(items, s.item)) return 'not_usable';
    if (seen.has(s.item)) return 'duplicate';
    seen.add(s.item);
    if (!Number.isSafeInteger(s.count) || s.count < 1 || s.count > dungeonStack(items, s.item)) return 'too_many';
  }
  if (storage === undefined) return null;
  const held = new Map(storage.map((s) => [s.item, s.count]));
  return bag.some((s) => (held.get(s.item) ?? 0) < s.count) ? 'not_in_storage' : null;
};
